import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch } from 'react-icons/fa';
import './AdminManagement.css';
import { API_ENDPOINTS } from '../../config/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  // Debug modal state
  useEffect(() => {
    console.log('Modal state changed:', showModal);
  }, [showModal]);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role_id: '',
    department_id: '',
    // Granular permissions
    can_manage_publications: false,
    can_manage_users: false,
    can_manage_departments: false,
    can_manage_publication_types: false,
    can_manage_categories: false,
    can_manage_document_types: false,
    can_manage_weekly_highlights: false,
    can_manage_legal_documents: false,
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Add keyboard event listener for Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        handleCloseModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const fetchData = async () => {
    try {
      console.log('Fetching data...');
      const [usersRes, rolesRes, departmentsRes] = await Promise.all([
        axios.get(API_ENDPOINTS.USERS),
        axios.get(API_ENDPOINTS.ROLES),
        axios.get(API_ENDPOINTS.DEPARTMENTS)
      ]);

      console.log('Data fetched successfully:', {
        users: usersRes.data.length,
        roles: rolesRes.data.length,
        departments: departmentsRes.data.length
      });

      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setDepartments(departmentsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingUser) {
        await axios.put(`${API_ENDPOINTS.USERS}/${editingUser.id}`, formData);
      } else {
        await axios.post(API_ENDPOINTS.USERS, formData);
      }
      
      fetchData();
      handleCloseModal();
    } catch (err) {
      console.error('User operation error:', err);
      setError(err.response?.data?.error || 'Operation failed');
      // Don't close modal on error so user can fix the issue
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`${API_ENDPOINTS.USERS}/${userId}`);
        fetchData();
      } catch (err) {
        setError(err.response?.data?.error || 'Delete failed');
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role_id: user.role_id,
      department_id: user.department_id,
      // Granular permissions
      can_manage_publications: user.can_manage_publications || false,
      can_manage_users: user.can_manage_users || false,
      can_manage_departments: user.can_manage_departments || false,
      can_manage_publication_types: user.can_manage_publication_types || false,
      can_manage_categories: user.can_manage_categories || false,
      can_manage_document_types: user.can_manage_document_types || false,
      can_manage_weekly_highlights: user.can_manage_weekly_highlights || false,
      can_manage_legal_documents: user.can_manage_legal_documents || false,
      is_active: user.is_active
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    console.log('Opening Add User modal');
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role_id: '',
      department_id: '',
      // Granular permissions
      can_manage_publications: false,
      can_manage_users: false,
      can_manage_departments: false,
      can_manage_publication_types: false,
      can_manage_categories: false,
      can_manage_document_types: false,
      can_manage_weekly_highlights: false,
      can_manage_legal_documents: false,
      is_active: true
    });
    setShowModal(true);
    setError(''); // Clear any previous errors
  };

  const handleCloseModal = () => {
    console.log('Closing modal');
    setShowModal(false);
    setEditingUser(null);
    setError('');
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.department_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-management">
      <div className="page-header">
        <h2>User Management</h2>
        <button className="add-button" onClick={handleAdd}>
          <FaPlus /> Add User
        </button>
      </div>

      <div className="search-bar">
        <div className="search-input">
          <FaSearch />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Department</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge role-${user.role_name?.toLowerCase().replace(' ', '-')}`}>
                    {user.role_name}
                  </span>
                </td>
                <td>{user.department_name || 'Not Assigned'}</td>
                <td>
                  <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(user)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(user.id)}
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

                   {/* Modal */}
      {showModal && (
        <div 
          onClick={handleCloseModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              zIndex: 10000
            }}
          >
             <div className="modal-header">
               <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
               <button className="close-button" onClick={handleCloseModal}>×</button>
             </div>
             
             <form onSubmit={handleSubmit} className="modal-form">
                               <div className="form-row">
                  <div className="form-group">
                    <label className="required">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="required">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                </div>

                               <div className="form-row">
                  <div className="form-group">
                    <label className={!editingUser ? "required" : ""}>
                      Password {editingUser ? '(leave blank to keep current)' : ''}
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required={!editingUser}
                    />
                  </div>
                  <div className="form-group">
                    <label className="required">Role</label>
                    <select
                      value={formData.role_id}
                      onChange={(e) => {
                        const selectedRoleId = e.target.value;
                        const selectedRole = roles.find(role => role.id == selectedRoleId);
                        
                        // Auto-assign all permissions for Super Admin
                        if (selectedRole && selectedRole.name === 'Super Admin') {
                          setFormData({
                            ...formData,
                            role_id: selectedRoleId,
                            can_manage_publications: true,
                            can_manage_users: true,
                            can_manage_departments: true,
                            can_manage_publication_types: true,
                            can_manage_categories: true,
                            can_manage_document_types: true,
                            can_manage_weekly_highlights: true,
                            can_manage_legal_documents: true
                          });
                        } else {
                          setFormData({...formData, role_id: selectedRoleId});
                        }
                      }}
                      required
                    >
                      <option value="">Select Role</option>
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

               <div className="form-row">
                 <div className="form-group">
                   <label>Department</label>
                   <select
                     value={formData.department_id}
                     onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                   >
                     <option value="">Select Department</option>
                     {departments.map(dept => (
                       <option key={dept.id} value={dept.id}>{dept.name}</option>
                     ))}
                   </select>
                 </div>
               </div>

               {/* Granular Permissions Section */}
                 <div className="form-group">
                 <label className="form-label fw-semibold mb-3">
                   🔐 Granular Permissions
                   {(() => {
                     const selectedRole = roles.find(role => role.id == formData.role_id);
                     if (selectedRole && selectedRole.name === 'Super Admin') {
                       return <span className="text-success ms-2">✨ All permissions automatically assigned for Super Admin</span>;
                     }
                     return null;
                   })()}
                 </label>
                 <div className="permissions-grid">
                   <div className="permission-item">
                     <label className="checkbox-label">
                       <input
                         type="checkbox"
                         checked={formData.can_manage_publications}
                         onChange={(e) => setFormData({...formData, can_manage_publications: e.target.checked})}
                         disabled={(() => {
                           const selectedRole = roles.find(role => role.id == formData.role_id);
                           return selectedRole && selectedRole.name === 'Super Admin';
                         })()}
                       />
                       Manage Publications
                     </label>
                   </div>
                   <div className="permission-item">
                     <label className="checkbox-label">
                       <input
                         type="checkbox"
                         checked={formData.can_manage_users}
                         onChange={(e) => setFormData({...formData, can_manage_users: e.target.checked})}
                         disabled={(() => {
                           const selectedRole = roles.find(role => role.id == formData.role_id);
                           return selectedRole && selectedRole.name === 'Super Admin';
                         })()}
                       />
                       Manage Users
                     </label>
                   </div>
                   <div className="permission-item">
                     <label className="checkbox-label">
                       <input
                         type="checkbox"
                         checked={formData.can_manage_departments}
                         onChange={(e) => setFormData({...formData, can_manage_departments: e.target.checked})}
                         disabled={(() => {
                           const selectedRole = roles.find(role => role.id == formData.role_id);
                           return selectedRole && selectedRole.name === 'Super Admin';
                         })()}
                       />
                       Manage Departments
                     </label>
                   </div>
                   <div className="permission-item">
                     <label className="checkbox-label">
                       <input
                         type="checkbox"
                         checked={formData.can_manage_publication_types}
                         onChange={(e) => setFormData({...formData, can_manage_publication_types: e.target.checked})}
                         disabled={(() => {
                           const selectedRole = roles.find(role => role.id == formData.role_id);
                           return selectedRole && selectedRole.name === 'Super Admin';
                         })()}
                       />
                       Manage Publication Types
                     </label>
                   </div>
                   <div className="permission-item">
                     <label className="checkbox-label">
                       <input
                         type="checkbox"
                         checked={formData.can_manage_categories}
                         onChange={(e) => setFormData({...formData, can_manage_categories: e.target.checked})}
                         disabled={(() => {
                           const selectedRole = roles.find(role => role.id == formData.role_id);
                           return selectedRole && selectedRole.name === 'Super Admin';
                         })()}
                       />
                       Manage Categories
                     </label>
                   </div>
                   <div className="permission-item">
                     <label className="checkbox-label">
                       <input
                         type="checkbox"
                         checked={formData.can_manage_document_types}
                         onChange={(e) => setFormData({...formData, can_manage_document_types: e.target.checked})}
                         disabled={(() => {
                           const selectedRole = roles.find(role => role.id == formData.role_id);
                           return selectedRole && selectedRole.name === 'Super Admin';
                         })()}
                       />
                       Manage Document Types
                     </label>
                   </div>
                   <div className="permission-item">
                     <label className="checkbox-label">
                       <input
                         type="checkbox"
                         checked={formData.can_manage_weekly_highlights}
                         onChange={(e) => setFormData({...formData, can_manage_weekly_highlights: e.target.checked})}
                         disabled={(() => {
                           const selectedRole = roles.find(role => role.id == formData.role_id);
                           return selectedRole && selectedRole.name === 'Super Admin';
                         })()}
                       />
                       Manage Weekly Highlights
                     </label>
                   </div>
                   <div className="permission-item">
                     <label className="checkbox-label">
                       <input
                         type="checkbox"
                         checked={formData.can_manage_legal_documents}
                         onChange={(e) => setFormData({...formData, can_manage_legal_documents: e.target.checked})}
                         disabled={(() => {
                           const selectedRole = roles.find(role => role.id == formData.role_id);
                           return selectedRole && selectedRole.name === 'Super Admin';
                         })()}
                       />
                       Manage Legal Documents
                     </label>
                   </div>
                 </div>
               </div>

               <div className="form-group">
                 <label className="checkbox-label">
                   <input
                     type="checkbox"
                     checked={formData.is_active}
                     onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                   />
                   Active User
                 </label>
               </div>

               <div className="modal-actions">
                 <button type="button" onClick={handleCloseModal} className="cancel-button">
                   Cancel
                 </button>
                 <button type="submit" className="save-button">
                   {editingUser ? 'Update' : 'Create'} User
                 </button>
               </div>
             </form>
           </div>
         </div>
       )}
    </div>
  );
};

export default UserManagement; 